(() => {
  const GLOBAL_STATE_KEY = '__b2hJourneyMeasurementV1';
  const existingState = window[GLOBAL_STATE_KEY];
  if (existingState?.initialize) {
    existingState.initialize();
    return;
  }
  const measurementState = { initialize: null };
  window[GLOBAL_STATE_KEY] = measurementState;

  const EVENT_NAMES = Object.freeze({
    careProductClick: 'b2h_care_product_click',
    launchProductView: 'b2h_launch_product_view',
    launchFormReady: 'b2h_launch_form_ready',
    launchSignupSuccess: 'b2h_launch_signup_success',
    customizationStart: 'b2h_customization_start',
    customizationError: 'b2h_customization_error',
    customizationReady: 'b2h_customization_ready',
    contextSelected: 'b2h:journey_context_selected_v1',
    routeSelected: 'b2h:journey_route_selected_v1',
    routeOpened: 'b2h:journey_route_opened_v1',
    routeViewed: 'b2h:journey_route_viewed_v1',
    outcome: 'b2h:journey_outcome_v1',
    decisionView: 'b2h_memorial_decision_view',
    decisionStart: 'b2h_memorial_decision_start',
    decisionAnswer: 'b2h_memorial_decision_answer',
    decisionComplete: 'b2h_memorial_decision_complete',
    decisionOutcomeView: 'b2h_memorial_decision_outcome_view',
    decisionPrimaryClick: 'b2h_memorial_decision_primary_click',
    decisionSecondaryClick: 'b2h_memorial_decision_secondary_click',
    supportView: 'b2h_support_guide_view',
    supportStart: 'b2h_support_guide_start',
    supportAnswer: 'b2h_support_guide_answer',
    supportComplete: 'b2h_support_guide_complete',
    supportOutcomeView: 'b2h_support_guide_outcome_view',
    supportPrimaryClick: 'b2h_support_guide_primary_click',
    supportSecondaryClick: 'b2h_support_guide_secondary_click',
  });

  const GA4_TARGET = 'G-773LG55S75';
  const RETAINED_GA4_EVENTS = Object.freeze({
    [EVENT_NAMES.careProductClick]: Object.freeze({ name: 'b2h_care_product_click', fields: Object.freeze(['product_id']) }),
    [EVENT_NAMES.launchProductView]: Object.freeze({ name: 'b2h_launch_product_view', fields: Object.freeze(['product_id']) }),
    [EVENT_NAMES.launchFormReady]: Object.freeze({ name: 'b2h_launch_form_ready', fields: Object.freeze(['product_id']) }),
    [EVENT_NAMES.launchSignupSuccess]: Object.freeze({ name: 'b2h_launch_signup_success', fields: Object.freeze(['product_id']) }),
    [EVENT_NAMES.customizationStart]: Object.freeze({ name: 'b2h_customization_start', fields: Object.freeze(['product_id', 'variant_id', 'input_mode']) }),
    [EVENT_NAMES.customizationError]: Object.freeze({ name: 'b2h_customization_error', fields: Object.freeze(['product_id', 'variant_id', 'input_mode', 'error_code']) }),
    [EVENT_NAMES.customizationReady]: Object.freeze({ name: 'b2h_customization_ready', fields: Object.freeze(['product_id', 'variant_id', 'input_mode']) }),
    [EVENT_NAMES.routeSelected]: Object.freeze({
      name: 'b2h_route_selected',
      fields: Object.freeze(['route', 'source']),
    }),
    [EVENT_NAMES.routeOpened]: Object.freeze({
      name: 'b2h_route_opened',
      fields: Object.freeze(['route', 'source']),
    }),
    [EVENT_NAMES.outcome]: Object.freeze({
      name: 'b2h_journey_outcome',
      fields: Object.freeze(['concept', 'future_use', 'outcome', 'path', 'route', 'source']),
    }),
    [EVENT_NAMES.decisionComplete]: Object.freeze({
      name: 'b2h_decision_complete',
      fields: Object.freeze(['buyer_role', 'identity_sources', 'moment', 'outcome_key', 'preferred_presence']),
    }),
    [EVENT_NAMES.decisionPrimaryClick]: Object.freeze({
      name: 'b2h_decision_primary_click',
      fields: Object.freeze(['destination_path', 'outcome_key']),
    }),
    [EVENT_NAMES.decisionSecondaryClick]: Object.freeze({
      name: 'b2h_decision_secondary_click',
      fields: Object.freeze(['destination_path', 'outcome_key']),
    }),
    [EVENT_NAMES.supportComplete]: Object.freeze({
      name: 'b2h_support_complete',
      fields: Object.freeze(['intention', 'known_context', 'outcome_key', 'relationship', 'timing']),
    }),
    [EVENT_NAMES.supportPrimaryClick]: Object.freeze({
      name: 'b2h_support_primary_click',
      fields: Object.freeze(['destination_path', 'outcome_key']),
    }),
    [EVENT_NAMES.supportSecondaryClick]: Object.freeze({
      name: 'b2h_support_secondary_click',
      fields: Object.freeze(['destination_path', 'outcome_key']),
    }),
  });

  const CONTEXTS = Object.freeze({
    together: 'pet_with_me',
    changing: 'pet_life_changing',
    recent: 'recent_loss',
    later: 'future_planning',
    support: 'supporting_someone',
  });

  const ROUTES = Object.freeze({
    preserve: 'preserve_memory',
    preserve_memory: 'preserve_memory',
    home: 'remember_at_home',
    remember_at_home: 'remember_at_home',
    close: 'keep_them_close',
    keep_them_close: 'keep_them_close',
    support: 'support_someone',
    support_someone: 'support_someone',
    support_offer: 'support_offer',
    senior_night_care: 'senior_night_care',
    end_of_life_prep: 'end_of_life_prep',
    daily_remembrance: 'daily_remembrance',
    vet_cost_resource: 'vet_cost_resource',
  });

  const ROUTE_PATHS = Object.freeze({
    '/pages/preserve-a-memory': 'preserve_memory',
    '/pages/remember-at-home': 'remember_at_home',
    '/pages/keep-them-close': 'keep_them_close',
    '/pages/support-someone': 'support_someone',
    '/pages/pet-loss-sympathy-gift': 'support_offer',
    '/pages/senior-night-care-checklist': 'senior_night_care',
    '/pages/end-of-life-conversation-prep': 'end_of_life_prep',
    '/pages/daily-pet-remembrance': 'daily_remembrance',
    '/pages/vet-cost-resource-map': 'vet_cost_resource',
  });

  const OUTCOMES = new Set([
    'concept_interest',
    'contact_support',
    'current_product_review',
    'current_products_review',
    'future_use_selected',
    'identity_followup_submit_attempt',
    'preservation_path_selected',
    'support_product_view',
    'continuity_resource_opened',
    'continuity_professional_resource_opened',
    'continuity_next_step_selected',
    'challenger_resource_opened',
  ]);

  const CONCEPTS = new Set(['capture', 'place', 'carry']);
  const PRESERVATION_PATHS = new Set(['capture_now', 'capture_kit', 'capture_plus_archive', 'existing_print', 'guide_only']);
  const FUTURE_USES = Object.freeze({
    preserve_only: 'preserve_only',
    home_remembrance: 'remember_at_home',
    private_memory_place: 'private_memory_place',
    portable_keepsake: 'keep_them_close',
    not_sure: 'not_sure',
    'Preserve it without choosing a product': 'preserve_only',
    'Make a remembrance for home': 'remember_at_home',
    'Create a private memory place': 'private_memory_place',
    'Carry a small keepsake': 'keep_them_close',
    'Not sure yet': 'not_sure',
  });

  const JOURNEY_SOURCES = new Set([
    'context_recommendation',
    'hero_support',
    'identity_intent',
    'portfolio_current',
    'portfolio_concept',
    'preserve_path',
    'route_choice',
    'route_next',
    'route_product',
    'route_result',
    'support_guide',
    'support_offer',
    'continuity_route',
    'professional_resource',
    'vet_cost_challenger',
  ]);

  const DECISION_BUYER_ROLES = new Set(['self', 'supporting_someone']);
  const DECISION_MOMENTS = new Set(['capture_now', 'recent_loss', 'remember_later', 'not_sure']);
  const DECISION_IDENTITY_SOURCES = new Set([
    'photo',
    'existing_print',
    'name_dates',
    'collar_tag',
    'story_routine',
    'not_sure',
  ]);
  const DECISION_PRESENCES = new Set(['daily_home', 'portable', 'private', 'recipient_choice', 'not_sure']);
  const DECISION_OUTCOMES = new Set([
    'recipient_control',
    'preservation_first',
    'current_home_product',
    'portrait_checklist',
    'private_no_purchase',
  ]);
  const DECISION_QUESTIONS = new Set(['buyer_role', 'moment', 'identity_source', 'preferred_presence']);
  const DECISION_DESTINATIONS = new Set([
    '/pages/support-someone',
    '/pages/preserve-a-memory',
    '/blogs/news/what-to-give-someone-who-lost-a-pet',
    '/blogs/news/how-to-preserve-an-existing-pet-paw-print',
    '/blogs/news/pet-memorial-at-home-guide',
    '/blogs/news/custom-pet-portrait-buyer-checklist',
    '/products/personalized-dog-memorial-suncatcher-ornament-loss-of-dog-sympathy-gift-for-dog-lover',
  ]);
  const SUPPORT_RELATIONSHIPS = new Set(['close_person', 'coworker_community', 'not_sure']);
  const SUPPORT_TIMINGS = new Set(['just_heard', 'follow_up', 'later_milestone']);
  const SUPPORT_CONTEXTS = new Set(['name_story_shared', 'practical_need_shared', 'little_known']);
  const SUPPORT_INTENTIONS = new Set(['words', 'practical', 'remembrance']);
  const SUPPORT_OUTCOMES = new Set([
    'acknowledge_now',
    'practical_support',
    'recipient_choice',
    'personalized_remembrance',
  ]);
  const SUPPORT_QUESTIONS = new Set(['relationship', 'timing', 'known_context', 'intention']);
  const SUPPORT_DESTINATIONS = new Set([
    '/blogs/news/what-to-say-when-someone-loses-a-pet',
    '/blogs/news/what-to-give-someone-who-lost-a-pet',
    '/pages/remember-at-home',
    '/pages/pet-loss-sympathy-gift',
  ]);

  const initializedRoots = new WeakSet();
  let privacyPromise;

  function normalizeRoute(value) {
    return ROUTES[value] || null;
  }

  function normalizeSource(value) {
    return JOURNEY_SOURCES.has(value) ? value : null;
  }

  function normalizeOutcome(value) {
    return OUTCOMES.has(value) ? value : null;
  }

  function routeFromLink(link) {
    const explicitRoute = normalizeRoute(link.dataset.b2hMeasurementRoute);
    if (explicitRoute) return explicitRoute;

    try {
      return ROUTE_PATHS[new URL(link.href, window.location.origin).pathname] || null;
    } catch (error) {
      return null;
    }
  }

  function routeForRoot(root) {
    const rootRoute = normalizeRoute(root.dataset.routeKey);
    if (rootRoute) return rootRoute;

    const selectedRoute = root.querySelector('[data-route][aria-pressed="true"]');
    return normalizeRoute(selectedRoute?.dataset.route);
  }

  function surfaceForRoot(root) {
    const surface = root.dataset.b2hMeasurementSurface;
    return [
      'north_star_home',
      'audience_route',
      'support_offer',
      'continuity_route',
      'challenger_route',
      'care_home',
      'launch_product',
      'product_studio',
    ].includes(surface) ? surface : null;
  }

  function environment() {
    return window.Shopify?.theme?.role === 'main' ? 'live' : 'preview';
  }

  function publishToRetainedGa4(eventName, payload) {
    const contract = RETAINED_GA4_EVENTS[eventName];
    if (!contract || payload.environment !== 'live' || !Array.isArray(window.dataLayer)) return false;

    const parameters = {
      b2h_schema_version: payload.schema_version,
      b2h_surface: payload.surface,
      send_to: GA4_TARGET,
    };
    for (const field of contract.fields) {
      if (typeof payload[field] === 'string') parameters[`b2h_${field}`] = payload[field];
    }

    if (new URLSearchParams(window.location.search).get('b2h_measurement_test') === '1') {
      parameters.b2h_measurement_context = 'qa';
    }

    try {
      const gtag = typeof window.gtag === 'function'
        ? window.gtag
        : function queueGtag() { window.dataLayer.push(arguments); };
      gtag('event', contract.name, parameters);
      return true;
    } catch (error) {
      return false;
    }
  }

  function loadCustomerPrivacy() {
    const currentPrivacy = window.Shopify?.customerPrivacy;
    if (currentPrivacy?.analyticsProcessingAllowed) return Promise.resolve(currentPrivacy);
    if (privacyPromise) return privacyPromise;

    privacyPromise = new Promise((resolve) => {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed) {
        resolve(window.Shopify.customerPrivacy);
        return;
      }

      if (typeof window.Shopify?.loadFeatures !== 'function') {
        resolve(null);
        return;
      }

      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => resolve(error ? null : window.Shopify?.customerPrivacy || null),
        );
      } catch (error) {
        resolve(null);
      }
    });

    return privacyPromise;
  }

  async function publish(eventName, root, eventData = {}) {
    const surface = surfaceForRoot(root);
    if (!surface || !Object.values(EVENT_NAMES).includes(eventName)) return false;

    // Do not replay an action that happened before analytics permission was
    // knowable or granted, even if the privacy API resolves after the action.
    let allowedAtAction = false;
    try {
      allowedAtAction = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (error) {
      return false;
    }
    if (!allowedAtAction) return false;

    const privacy = await loadCustomerPrivacy();
    if (!privacy?.analyticsProcessingAllowed?.()) return false;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return false;

    const payload = {
      schema_version: 1,
      consent_state: 'analytics_allowed',
      environment: environment(),
      surface,
      ...eventData,
    };

    try {
      const result = window.Shopify.analytics.publish(eventName, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
      publishToRetainedGa4(eventName, payload);
      return true;
    } catch (error) {
      return false;
    }
  }

  function publishRouteOpened(root, link) {
    const route = routeFromLink(link);
    const source = normalizeSource(link.dataset.b2hMeasurementSource) || 'route_next';

    if (route) {
      void publish(EVENT_NAMES.routeOpened, root, { route, source });
      return;
    }

    try {
      if (new URL(link.href, window.location.origin).pathname === '/pages/contact-us') {
        void publish(EVENT_NAMES.outcome, root, {
          outcome: 'contact_support',
          route: routeForRoot(root),
          source,
        });
      }
    } catch (error) {
      // Invalid or absent destinations are not measurement events.
    }
  }

  function publishOutcome(root, node) {
    const outcome = normalizeOutcome(node.dataset.b2hMeasurementOutcome);
    const source = normalizeSource(node.dataset.b2hMeasurementSource);
    if (!outcome || !source) return;

    const eventData = { outcome, source };
    const route = normalizeRoute(node.dataset.b2hMeasurementRoute) || routeForRoot(root);
    const concept = node.dataset.b2hMeasurementConcept;
    if (route) eventData.route = route;
    if (CONCEPTS.has(concept)) eventData.concept = concept;
    void publish(EVENT_NAMES.outcome, root, eventData);
  }

  function onRootClick(root, event) {
    const contextButton = event.target.closest('[data-context]');
    if (contextButton && root.contains(contextButton)) {
      const context = CONTEXTS[contextButton.dataset.context];
      const route = normalizeRoute(contextButton.dataset.routeTarget);
      if (!context || !route) return;
      void publish(EVENT_NAMES.contextSelected, root, {
        context,
        recommended_route: route,
      });
      void publish(EVENT_NAMES.routeSelected, root, {
        route,
        source: 'context_recommendation',
      });
      return;
    }

    const routeButton = event.target.closest('[data-route]');
    if (routeButton && root.contains(routeButton)) {
      const route = normalizeRoute(routeButton.dataset.route);
      if (route) {
        void publish(EVENT_NAMES.routeSelected, root, {
          route,
          source: 'route_choice',
        });
      }
      return;
    }

    const routeLink = event.target.closest('[data-b2h-measurement-route-link]');
    if (routeLink && root.contains(routeLink)) {
      publishRouteOpened(root, routeLink);
      return;
    }

    const preservePath = event.target.closest('[data-preserve-intent]');
    if (preservePath && root.contains(preservePath)) {
      const path = preservePath.dataset.preserveIntent;
      if (PRESERVATION_PATHS.has(path)) {
        void publish(EVENT_NAMES.outcome, root, {
          outcome: 'preservation_path_selected',
          path,
          route: routeForRoot(root),
          source: 'preserve_path',
        });
      }
      return;
    }

    const conceptInterest = event.target.closest('[data-concept-interest]');
    if (conceptInterest && root.contains(conceptInterest)) {
      const dialog = conceptInterest.closest('[data-concept-dialog]');
      const concept = dialog?.dataset.b2hMeasurementConcept;
      if (CONCEPTS.has(concept)) {
        void publish(EVENT_NAMES.outcome, root, {
          concept,
          outcome: 'concept_interest',
          route: routeForRoot(root),
          source: 'portfolio_concept',
        });
      }
      return;
    }

    const outcomeNode = event.target.closest('[data-b2h-measurement-outcome]');
    if (outcomeNode && root.contains(outcomeNode)) publishOutcome(root, outcomeNode);
  }

  function onRootChange(root, event) {
    const mobileRouteSelect = event.target.closest('[data-mot-select]');
    if (mobileRouteSelect && root.contains(mobileRouteSelect)) {
      const routeButton = [...root.querySelectorAll('[data-context][data-route]')]
        .find((button) => button.dataset.route === mobileRouteSelect.value);
      const context = CONTEXTS[routeButton?.dataset.context];
      const route = normalizeRoute(routeButton?.dataset.routeTarget);
      if (!context || !route) return;
      void publish(EVENT_NAMES.contextSelected, root, {
        context,
        recommended_route: route,
      });
      void publish(EVENT_NAMES.routeSelected, root, {
        route,
        source: 'context_recommendation',
      });
      return;
    }

    const identityPath = event.target.closest('[data-identity-path]');
    if (identityPath?.checked && root.contains(identityPath)) {
      const path = identityPath.dataset.identityPath;
      if (PRESERVATION_PATHS.has(path)) {
        void publish(EVENT_NAMES.outcome, root, {
          outcome: 'preservation_path_selected',
          path,
          route: routeForRoot(root),
          source: 'identity_intent',
        });
      }
      return;
    }

    const futureUse = event.target.closest('[data-b2h-measurement-future-use]');
    if (!futureUse || !root.contains(futureUse)) return;
    const futureUseKey = FUTURE_USES[futureUse.value];
    if (!futureUseKey) return;
    void publish(EVENT_NAMES.outcome, root, {
      future_use: futureUseKey,
      outcome: 'future_use_selected',
      route: routeForRoot(root),
      source: 'identity_intent',
    });
  }

  function onRootSubmit(root, event) {
    const form = event.target.closest('.b2h-identity-intent__form');
    if (!form || !root.contains(form) || !form.checkValidity()) return;

    const path = form.querySelector('[data-identity-path]:checked')?.dataset.identityPath;
    const futureUseValue = form.querySelector('[data-b2h-measurement-future-use]')?.value;
    const futureUse = FUTURE_USES[futureUseValue];
    if (!PRESERVATION_PATHS.has(path) || !futureUse) return;

    void publish(EVENT_NAMES.outcome, root, {
      future_use: futureUse,
      outcome: 'identity_followup_submit_attempt',
      path,
      route: routeForRoot(root),
      source: 'identity_intent',
    });
  }

  function decisionAnswerAllowed(question, answer) {
    if (question === 'buyer_role') return DECISION_BUYER_ROLES.has(answer);
    if (question === 'moment') return DECISION_MOMENTS.has(answer);
    if (question === 'identity_source') return DECISION_IDENTITY_SOURCES.has(answer);
    if (question === 'preferred_presence') return DECISION_PRESENCES.has(answer);
    return false;
  }

  function normalizeDecisionSources(values) {
    if (!Array.isArray(values) || values.length === 0) return null;
    const sources = [...new Set(values)].filter((value) => DECISION_IDENTITY_SOURCES.has(value)).sort();
    if (sources.length !== values.length) return null;
    if (sources.includes('not_sure') && sources.length > 1) return null;
    return sources.join('|');
  }

  function onDecisionEvent(root, event) {
    const detail = event.detail;
    if (!detail || typeof detail !== 'object') return;
    const pagePath = window.location.pathname;
    if (pagePath !== '/pages/remember-at-home') return;

    if (detail.action === 'view') {
      void publish(EVENT_NAMES.decisionView, root, { page_path: pagePath });
      return;
    }
    if (detail.action === 'start') {
      void publish(EVENT_NAMES.decisionStart, root, { page_path: pagePath });
      return;
    }
    if (detail.action === 'answer') {
      if (!DECISION_QUESTIONS.has(detail.question) || !decisionAnswerAllowed(detail.question, detail.answer)) return;
      const payload = {
        answer: detail.answer,
        page_path: pagePath,
        question: detail.question,
      };
      if (detail.question === 'identity_source') payload.selected = detail.selected === true;
      void publish(EVENT_NAMES.decisionAnswer, root, payload);
      return;
    }

    const outcomeKey = DECISION_OUTCOMES.has(detail.outcome_key) ? detail.outcome_key : null;
    if (detail.action === 'complete') {
      const identitySources = normalizeDecisionSources(detail.identity_sources);
      if (
        !DECISION_BUYER_ROLES.has(detail.buyer_role)
        || !DECISION_MOMENTS.has(detail.moment)
        || !DECISION_PRESENCES.has(detail.preferred_presence)
        || !identitySources
        || !outcomeKey
      ) return;
      void publish(EVENT_NAMES.decisionComplete, root, {
        buyer_role: detail.buyer_role,
        identity_sources: identitySources,
        moment: detail.moment,
        outcome_key: outcomeKey,
        page_path: pagePath,
        preferred_presence: detail.preferred_presence,
      });
      return;
    }
    if (detail.action === 'outcome_view' && outcomeKey) {
      void publish(EVENT_NAMES.decisionOutcomeView, root, { outcome_key: outcomeKey, page_path: pagePath });
      return;
    }
    if ((detail.action === 'primary_click' || detail.action === 'secondary_click') && outcomeKey) {
      const destinationPath = DECISION_DESTINATIONS.has(detail.destination_path) ? detail.destination_path : null;
      if (!destinationPath) return;
      const eventName = detail.action === 'primary_click'
        ? EVENT_NAMES.decisionPrimaryClick
        : EVENT_NAMES.decisionSecondaryClick;
      void publish(eventName, root, {
        destination_path: destinationPath,
        outcome_key: outcomeKey,
        page_path: pagePath,
      });
    }
  }

  function supportAnswerAllowed(question, answer) {
    if (question === 'relationship') return SUPPORT_RELATIONSHIPS.has(answer);
    if (question === 'timing') return SUPPORT_TIMINGS.has(answer);
    if (question === 'known_context') return SUPPORT_CONTEXTS.has(answer);
    if (question === 'intention') return SUPPORT_INTENTIONS.has(answer);
    return false;
  }

  function onSupportGuideEvent(root, event) {
    const detail = event.detail;
    if (!detail || typeof detail !== 'object') return;
    const pagePath = window.location.pathname;
    if (pagePath !== '/pages/support-someone') return;

    if (detail.action === 'view') {
      void publish(EVENT_NAMES.supportView, root, { page_path: pagePath });
      return;
    }
    if (detail.action === 'start') {
      void publish(EVENT_NAMES.supportStart, root, { page_path: pagePath });
      return;
    }
    if (detail.action === 'answer') {
      if (!SUPPORT_QUESTIONS.has(detail.question) || !supportAnswerAllowed(detail.question, detail.answer)) return;
      void publish(EVENT_NAMES.supportAnswer, root, {
        answer: detail.answer,
        page_path: pagePath,
        question: detail.question,
      });
      return;
    }

    const outcomeKey = SUPPORT_OUTCOMES.has(detail.outcome_key) ? detail.outcome_key : null;
    if (detail.action === 'complete') {
      if (
        !SUPPORT_RELATIONSHIPS.has(detail.relationship)
        || !SUPPORT_TIMINGS.has(detail.timing)
        || !SUPPORT_CONTEXTS.has(detail.known_context)
        || !SUPPORT_INTENTIONS.has(detail.intention)
        || !outcomeKey
      ) return;
      void publish(EVENT_NAMES.supportComplete, root, {
        intention: detail.intention,
        known_context: detail.known_context,
        outcome_key: outcomeKey,
        page_path: pagePath,
        relationship: detail.relationship,
        timing: detail.timing,
      });
      return;
    }
    if (detail.action === 'outcome_view' && outcomeKey) {
      void publish(EVENT_NAMES.supportOutcomeView, root, { outcome_key: outcomeKey, page_path: pagePath });
      return;
    }
    if ((detail.action === 'primary_click' || detail.action === 'secondary_click') && outcomeKey) {
      const destinationPath = SUPPORT_DESTINATIONS.has(detail.destination_path) ? detail.destination_path : null;
      if (!destinationPath) return;
      const eventName = detail.action === 'primary_click'
        ? EVENT_NAMES.supportPrimaryClick
        : EVENT_NAMES.supportSecondaryClick;
      void publish(eventName, root, {
        destination_path: destinationPath,
        outcome_key: outcomeKey,
        page_path: pagePath,
      });
    }
  }

  function initializeLaunch(root, surface) {
    const productId = node => /^\d{10,16}$/.test(node?.dataset.b2hLaunchProduct || '')
      ? node.dataset.b2hLaunchProduct : null;
    if (surface === 'care_home') {
      root.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        const card = link?.closest('[data-b2h-launch-product]');
        const id = productId(card);
        if (!id || !root.contains(card)) return;
        void publish(EVENT_NAMES.careProductClick, root, { product_id: id });
      });
      return;
    }
    const id = productId(root);
    if (!id) return;
    void publish(EVENT_NAMES.launchProductView, root, { product_id: id });
    const readyBridges = new WeakSet();
    let readySent = false;
    let successSeen = false;
    const observeState = bridge => {
      const state = bridge.dataset.b2hFormsState;
      if (state === 'ready') {
        readyBridges.add(bridge);
        if (!readySent) {
          readySent = true;
          void publish(EVENT_NAMES.launchFormReady, root, { product_id: id });
        }
      } else if (state === 'success' && !successSeen) {
        // A restored success panel is not a new registration.
        successSeen = true;
        if (readyBridges.has(bridge)) {
          void publish(EVENT_NAMES.launchSignupSuccess, root, { product_id: id });
        }
      }
    };
    root.addEventListener('b2h:forms:state', event => {
      const bridge = event.target.closest('[data-b2h-forms-observe-state]');
      if (bridge && root.contains(bridge)) observeState(bridge);
    });
    root.querySelectorAll('[data-b2h-forms-observe-state]').forEach(observeState);
  }

  function initializePhotoStudio(root) {
    if (root.dataset.photoPortraitOnly !== 'true') return;
    const productId = root.dataset.b2hProductId;
    if (!/^\d{10,16}$/.test(productId || '')) return;
    const form = root.querySelector('form.b2h-v3-product-studio__form');
    const photo = form?.querySelector('[data-photo-upload]:not(:disabled)');
    const name = form?.querySelector('[data-review-source="pet-name"]:not(:disabled)');
    const rights = form?.querySelector('[data-photo-rights]:not(:disabled)');
    const workflow = form?.querySelector('[data-photo-workflow]');
    const preview = root.querySelector('[data-live-preview]');
    if (!form || !photo || !name || !rights || !workflow || !preview) return;

    const sent = { start: false, ready: false };
    const pending = { start: null, ready: null };
    const errorCodes = new Set();
    const pendingErrors = new Set();
    let attempt = 0;
    let photoChangeAllowed = false;
    const variantId = () => {
      const value = form.querySelector('[data-variant-id]')?.value;
      return /^\d{10,16}$/.test(value || '') ? value : null;
    };
    const eventData = (extra = {}) => {
      const id = variantId();
      if (!id) return null;
      return { product_id: productId, variant_id: id, input_mode: 'photo', ...extra };
    };
    const emitOnce = async (key, eventName) => {
      if (sent[key] || pending[key] === attempt) return;
      const payload = eventData();
      if (!payload) return;
      const ticket = attempt;
      pending[key] = ticket;
      const published = await publish(eventName, root, payload);
      if (pending[key] === ticket) pending[key] = null;
      if (published && ticket === attempt) sent[key] = true;
    };
    const emitError = async (code) => {
      const payload = eventData({ error_code: code });
      if (!payload) return;
      const ticket = attempt;
      const pendingKey = `${ticket}:${code}`;
      if (errorCodes.has(code) || pendingErrors.has(pendingKey)) return;
      pendingErrors.add(pendingKey);
      const published = await publish(EVENT_NAMES.customizationError, root, payload);
      pendingErrors.delete(pendingKey);
      if (published && ticket === attempt) errorCodes.add(code);
    };
    const errorCode = () => {
      if (photo.validity.customError) return 'photo_invalid';
      if (photo.validity.valueMissing) return 'photo_missing';
      if (name.validity.valueMissing) return 'name_missing';
      if (rights.validity.valueMissing) return 'rights_missing';
      return 'other_required';
    };
    const isReady = () => root.dataset.photoState === 'ready'
      && workflow.dataset.workflowState === 'review'
      && !preview.hidden
      && form.matches(':valid');
    const isMeaningful = (field) => {
      if (field === photo) return photo.files?.length > 0;
      if (field === name) return Boolean(name.value.trim());
      if (field === rights) return rights.checked;
      if (field.matches('[data-photo-crop], [data-photo-control], [data-review-source="photo-treatment"]')) return true;
      return false;
    };
    const onFieldChange = (event) => {
      const field = event.target;
      if (field === form) {
        // The studio emits a form-level change after asynchronous image decode.
        // Only credit that transition when analytics was allowed at upload time.
        requestAnimationFrame(() => {
          if (photoChangeAllowed && isReady()) void emitOnce('ready', EVENT_NAMES.customizationReady);
        });
        return;
      }
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement) || !form.contains(field) || !isMeaningful(field)) return;
      if (field === photo) {
        try {
          photoChangeAllowed = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
        } catch (error) {
          photoChangeAllowed = false;
        }
      }
      void emitOnce('start', EVENT_NAMES.customizationStart);
      // The studio updates its native validation and review state in its own
      // form listeners. Check after those handlers, never from a preview render.
      requestAnimationFrame(() => {
        if (isReady()) void emitOnce('ready', EVENT_NAMES.customizationReady);
      });
    };
    form.addEventListener('input', onFieldChange);
    form.addEventListener('change', onFieldChange);
    form.addEventListener('click', (event) => {
      if (!event.target.closest('[data-submit-button]') || form.matches(':valid')) return;
      void emitError(errorCode());
    }, true);
    form.addEventListener('reset', () => {
      attempt += 1;
      photoChangeAllowed = false;
      sent.start = false;
      sent.ready = false;
      errorCodes.clear();
    });
  }

  function initializeRoot(root) {
    if (initializedRoots.has(root)) return;
    if (root.dataset.b2hMeasurementEnabled !== 'true') return;
    const surface = surfaceForRoot(root);
    if (!surface) return;

    initializedRoots.add(root);
    void loadCustomerPrivacy();
    if (surface === 'care_home' || surface === 'launch_product') {
      initializeLaunch(root, surface);
      return;
    }
    if (surface === 'product_studio') {
      initializePhotoStudio(root);
      return;
    }
    root.addEventListener('click', (event) => onRootClick(root, event));
    root.addEventListener('change', (event) => onRootChange(root, event));
    root.addEventListener('submit', (event) => onRootSubmit(root, event));
    root.addEventListener('b2h:memorial-decision', (event) => onDecisionEvent(root, event));
    root.addEventListener('b2h:support-guide', (event) => onSupportGuideEvent(root, event));

    const route = routeForRoot(root);
    if (surface !== 'north_star_home' && route) {
      void publish(EVENT_NAMES.routeViewed, root, { route });
    }
  }

  function initialize() {
    for (const root of document.querySelectorAll('[data-b2h-measurement-root]')) {
      initializeRoot(root);
    }
  }

  measurementState.initialize = initialize;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
